import { createContext, useContext, useState, useEffect } from "react";
import { useMutation, useLazyQuery } from "@apollo/client";
import {
  GET_SHIPPING_LABELS_QUERY,
  GET_SHIPPING_LABEL_QUERY,
} from "../api/queries/shipping-label.queries";
import { CREATE_SHIPPING_LABEL_MUTATION } from "../api/mutations/shipping-label.mutations";
import { MessagesContext } from "./messages.context";
import { UserContext } from "./user.context";

export const ShippingLabelsContext = createContext({
  shippingLabels: [],
  setShippingLabels: () => null,
  createShippingLabel: () => null,
  fetchShippingLabels: () => null,
});

export const ShippingLabelsProvider = ({ children }) => {
  const { currentUser, getUserAddresses } = useContext(UserContext);
  const { addMessage } = useContext(MessagesContext);
  const [shippingLabels, setShippingLabels] = useState([]);
  const [contactIds, setContactIds] = useState(null);
  const [createShippingLabelMutation] = useMutation(
    CREATE_SHIPPING_LABEL_MUTATION
  );
  const [getShippingLabel] = useLazyQuery(GET_SHIPPING_LABEL_QUERY);
  const [getShippingLabels, { refetch: refetchShippingLabels }] = useLazyQuery(
    GET_SHIPPING_LABELS_QUERY
  );

  useEffect(() => {
    if (!currentUser?.email || currentUser.role === "admin") return;
    getUserAddresses(currentUser.email).then((addresses) => {
      if (addresses?.length) {
        setContactIds(addresses.map((a) => a.id));
      }
    });
  }, [currentUser?.email]);

  const buildVariables = (sort, direction) => ({
    customer_id: currentUser.customer_id,
    contact_ids: currentUser.role === "admin" ? null : contactIds,
    sort: sort ? `${direction === "asc" ? "" : "-"}${sort}` : null,
  });

  const fetchShippingLabels = async (sort, direction, filter, refetch) => {
    try {
      if (refetch) {
        const response = await refetchShippingLabels({
          variables: buildVariables(sort, direction),
        });
        if (!response || response.error)
          throw new Error("Error fetching shipping labels");
        return response.data.shippingLabels;
      }

      const response = await getShippingLabels(
        { variables: buildVariables(sort, direction) },
        { networkPolicy: "network-only" }
      );
      if (!response || response.error)
        throw new Error("Error fetching shipping labels");
      return response.data.shippingLabels;
    } catch (error) {
      addMessage(error.message, "error");
    }
  };

  const createShippingLabel = async (contact) => {
    try {
      addMessage("Creating shipping label...", "success");
      const response = await createShippingLabelMutation({
        variables: {
          contact_id: contact?.id ? Number(contact.id) : undefined,
          name: contact?.name,
          address1: contact?.address1,
          address2: contact?.address2,
          city: contact?.city,
          state: contact?.state,
          zip: contact?.zip,
        },
      });
      if (!response?.data || response?.errors)
        throw new Error("Error creating shipping label");
      setShippingLabels([...shippingLabels, response.data.createShippingLabel]);
      addMessage("Shipping label created", "success");
      return response.data.createShippingLabel;
    } catch (error) {
      addMessage(error.message, "error");
    }
  };

  const fetchShippingLabel = async (id) => {
    try {
      const response = await getShippingLabel(
        {
          variables: {
            _id: id,
          },
        },
        {
          networkPolicy: "network-only",
        }
      );
      if (!response || response.error)
        throw new Error("Error fetching shipping label");
      return response.data.shippingLabel;
    } catch (error) {
      addMessage(error.message, "error");
    }
  };

  const value = {
    shippingLabels,
    setShippingLabels,
    createShippingLabel,
    fetchShippingLabels,
    fetchShippingLabel,
  };
  return (
    <ShippingLabelsContext.Provider value={value}>
      {children}
    </ShippingLabelsContext.Provider>
  );
};
